import { object } from 'yup';
import {
  IEntityType,
  ITableInterface,
  ITableData,
  ITableChildrenInterface,
  ITableMetricMap,
  ISortOrder,
  IPageConfigLineMetric,
  IPageConfigTableMetric,
  IGetLineDataParams,
  ILineChartInterface,
  ILineChartData,
} from '../../typings';
import { isEmptyValue } from '@home/utils/common';

export const FETCH_KEY_MAP: any = {
  genreType: 'genre_tab',
  genreSource: 'genre_source',
  regions: 'regions',
  markets: 'markets',
  startDate: 'start_date',
  endDate: 'end_date',
  sourceMetrics: 'source_metrics',
};

export const PAGE_LOG_CONFIG_KEYS: any = {
  ['mobile_main']: 'genreProfileMobileGameMainGenre',
  ['mobile_sub']: 'genreProfileMobileGameSubGenre',
  ['mobile_mainsub']: 'genreProfileMobileGameMainsubGenre',
  ['pc_main']: 'genreProfilePCGameMainGenre',
  ['pc_sub']: 'genreProfilePCGameSubGenre',
  ['pc_mainsub']: 'genreProfilePCGameMainsubGenre',
  ['console_main']: 'genreProfileConsoleGameMainGenre',
  ['console_sub']: 'genreProfileConsoleGameSubGenre',
  ['console_mainsub']: 'genreProfileConsoleGameMainsubGenre',
  ['total_main']: 'genreProfileTotalGameMainGenre',
  ['total_sub']: 'genreProfileTotalGameSubGenre',
  ['total_mainsub']: 'genreProfileTotalGameMainsubGenre',
};

export const MARKET_GENRE_CONFIG: any = {
  common: {
    METRIC_NAME_MAP: {
      id: 'id',
      gametitle: 'mainGenre',
      genre: 'genre',
      gametitleCn: 'mainGenreCn',
      subName: 'subGenre',
      sub_genre: 'sub_genre',
      subNameCn: 'subGenreCn',
      metricList: 'metric_list',
      abbr: '',
      cover: '',
      publisher: '',
      countryIcon: '',
      metricTitle: 'title',
      metricSource: 'source',
      metricName: 'metric',
      metricRatio: 'growth_ratio',
      metricHistory: 'history',
      metricValue: 'value',
    },
    getTrueKey: (_sortKey: string) => {
      // const len = _sortKey.length;
      return {
        trueKey: _sortKey,
        sortType: _sortKey,
      };
    },
    /**
     * 表格的排序方法，写在里面的原因是品类为main的时候，还需要对children进行排序
     * @param _list 原始表格数据
     * @param _sortKey 排序的key
     * @param _sortOrder 排序的方式：desc、asc
     * @param noRank 是否有排序，不需要排序可能也需要占位
     * @returns
     */
    sortFn: (
      _list: ITableData[],
      _sortKey: string,
      _sortOrder: ISortOrder,
      noRank: boolean,
    ): ITableData[] => {
      // const { trueKey, sortType } = _that.common.getTrueKey(_sortKey);
      // const findMetric = (
      //   metricList: ITableChildrenInterface[],
      //   key: string,
      //   typeStr: 'value' | 'growth_ratio',
      // ) => {
      //   const find = metricList?.find((item) => item.metric === key);
      //   if (find) return find[typeStr] || 0;
      //   return 0;
      // };
      // // 对比方法
      const compareFun = (a: any, b: any) => {
        // const typeStr = sortType === '0' ? 'value' : 'growth_ratio';
        const aValue = a.metrics[_sortKey];
        const bValue = b.metrics[_sortKey];
        const isAValueEmpty = isEmptyValue(aValue);
        const isBValueEmpty = isEmptyValue(bValue);
        if (isAValueEmpty && isBValueEmpty) return 0;
        if (isAValueEmpty) return 1;
        if (isBValueEmpty) return -1;
        // const bValue = findMetric(b.metric_list, trueKey, typeStr);
        return _sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      };

      return _list.sort(compareFun)?.map((item, index) =>
        Object.assign(item, {
          id: noRank ? null : index + 1,
        }),
      );
    },
    getCommonFirstColConfig: () => {
      const getTableFirstColConfig = ({
        title,
        type,
        width,
        key,
      }: {
        title: string;
        type: string;
        width?: number;
        key: string;
      }) => {
        return {
          title,
          key,
          type,
          hasSort: false,
          defaultSort: false,
          hasCheckbox: true,
          hasRank: true,
          canBeClick: true,
          width,
        };
      };
      return {
        main: [
          {
            title: 'dbi_main_genre',
            key: 'genre',
            type: 'text',
            hasSort: false,
            defaultSort: false,
            hasCheckbox: true,
            hasRank: true,
            canBeClick: true,
          },
        ],
        sub: [
          getTableFirstColConfig({
            title: 'dbi_sub_genre',
            type: 'text',
            key: 'sub_genre',
          }),
        ],
        mainsub: [
          getTableFirstColConfig({
            title: 'dbi_main_genre',
            type: 'text',
            key: 'genre',
          }),
          {
            title: 'dbi_sub_genre',
            type: 'text',
            key: 'sub_genre',
            hasSort: false,
            defaultSort: false,
            hasCheckbox: false,
            align: 'center',
            childWidth: 210,
            childAlign: 'left',
            hasRank: false,
            canBeClick: true,
          },
        ],
      };
    },
    getCommonSepcialColConfig: () => {
      return {
        main: [
          {
            type: 'action',
            title: 'Actions',
            key: 'Details',
            // 跳转的时候使用的key
            clickKey: 'genre',
            linkUrl: '',
            align: 'center',
            childWidth: 100,
            childAlign: 'left',
          },
        ],
        sub: [
          {
            type: 'action',
            title: 'Actions',
            // 跳转的时候使用的key
            clickKey: 'sub_genre',
            key: 'Details',
            linkUrl: '',
            align: 'center',
            childWidth: 100,
            childAlign: 'left',
          },
        ],
        mainsub: null,
      };
    },
    getCommonLineMetricList: (type: IEntityType) => {
      const getMetrics = (_type: IEntityType): IPageConfigLineMetric[] => {
        switch (_type) {
          case 'mobile':
            return [
              {
                text: 'dbi_revenue_$',
                value: 'revenue',
                source: '',
              },
              {
                text: 'dbi_downloads',
                value: 'download',
                source: '',
              },
            ];
          case 'total':
            return [
              {
                text: 'dbi_revenue_$',
                value: 'revenue',
                source: '',
              },
              {
                text: 'dbi_genre_profile_metric_mobile_downloads',
                value: 'download',
                source: '',
              },
            ];
          default:
            return [
              {
                text: 'dbi_metric_digitalRevenue',
                value: 'digital_revenue',
                source: 'mscience',
              },
              {
                text: 'dbi_metric_physical_revenue',
                value: 'physical_revenue',
                source: 'npd',
              },
            ];
        }
      };
      const metrics = getMetrics(type);
      return {
        main: metrics,
        sub: metrics,
        mainsub: metrics,
      };
    },
    getCommonTableMetricList: (type: IEntityType, filter?: any) => {
      const getMetrics = (_type: IEntityType, _filter: any): IPageConfigTableMetric[] => {
        switch (_type) {
          case 'mobile':
            return [
              {
                title: 'dbi_revenue_$',
                source: filter.sourceMetrics,
                metric: `${filter.sourceMetrics}_revenue`,
                align: 'center',
                childWidth: 200,
                childAlign: 'right',
                isNeedSort: true,
              },
              {
                title: 'dbi_downloads',
                source: filter.sourceMetrics,
                metric: `${filter.sourceMetrics}_download`,
                align: 'center',
                childWidth: 190,
                childAlign: 'right',
                isNeedSort: true,
              },
            ];
          case 'total':
            return [
              {
                title: 'dbi_total_rev_$',
                source: '',
                metric: 'total_revenue',
                align: 'center',
                childWidth: 210,
                childAlign: 'right',
                isNeedSort: true,
              },
              {
                title: 'dbi_downloads',
                source: filter.sourceMetrics,
                metric: `${filter.sourceMetrics}_download`,
                align: 'center',
                childWidth: 190,
                childAlign: 'right',
                isNeedSort: true,
              },
            ];
          default:
            return [
              {
                title: 'dbi_metric_digitalRevenue',
                source: 'mscience',
                metric: 'digital_revenue',
                align: 'center',
                childWidth: 240,
                childAlign: 'right',
                isNeedSort: true,
              },
              {
                title: 'dbi_metric_physical_revenue',
                source: 'npd',
                metric: 'physical_revenue',
                align: 'center',
                childWidth: 250,
                childAlign: 'right',
                isNeedSort: true,
              },
            ];
        }
      };
      const metrics = getMetrics(type, filter);
      return {
        main: metrics,
        sub: metrics,
        mainsub: metrics,
      };
    },
    getCommonKeyMap: (type: IEntityType) => {
      const metrics = _that.common.METRIC_NAME_MAP;
      return {
        main: Object.assign({}, metrics, {
          expandParentKey: 'genre',
          expandChildKey: 'sub_genre',
        }),
        sub: Object.assign({}, metrics, {
          gametitle: 'genre',
          gametitleCn: 'sub_genre',
        }),
        mainsub: metrics,
      };
    },
    getTableData: () => {
      const getMetricFormat = (
        fetchList: ITableChildrenInterface[],
        metricMapList: ITableMetricMap[],
      ): ITableChildrenInterface[] => {
        return metricMapList?.map((configItem) => {
          const { metric } = configItem;
          const find = fetchList?.find((fetchMetricItem) => fetchMetricItem.metric === metric);
          if (find) return Object.assign(find, configItem);
          return {};
        });
      };
      const getOriginalTableDataWhenMainSub = (
        _list: ITableInterface[],
        _metricMap: ITableMetricMap[],
        type: string,
      ): ITableData[] => {
        const tableTmp: ITableData[] = [];
        _list?.forEach((mainGenreItem: ITableInterface) => {
          const { genre_name = '', genre_cn_name = '', children = [] } = mainGenreItem || {};
          (children || []).forEach((subGenreItem: ITableInterface) => {
            const {
              name = '',
              cn_name = '',
              metric_list: fetchMetricList = [],
              top_games = [],
            } = subGenreItem;
            tableTmp.push({
              searchKey: type === 'sub' ? name : `${genre_name}-${name}`,
              searchKeyCn: type === 'sub' ? cn_name : `${genre_cn_name}-${cn_name}`,
              mainGenre: genre_name,
              mainGenreCn: genre_cn_name,
              subGenre: name,
              subGenreCn: cn_name,
              metric_list: getMetricFormat(fetchMetricList, _metricMap),
              topGames: top_games,
            });
          });
        });
        return tableTmp;
      };
      const getOriginalTableDataWhenSub = (
        _list: ITableInterface[],
        _metricMap: ITableMetricMap[],
      ): ITableData[] => {
        return _list?.map((mainGenreItem: ITableInterface) => {
          const {
            genre_name = '',
            genre_cn_name = '',
            children = [],
            metric_list: fetchParentList = [],
            top_games = [],
          } = mainGenreItem || {};
          return {
            subGenre: genre_name,
            subGenreCn: genre_cn_name,
            searchKey: genre_name,
            searchKeyCn: genre_cn_name,
            metric_list: getMetricFormat(fetchParentList, _metricMap),
            topGames: top_games,
          };
        });
      };
      const getOriginalTableDataWhenMain = (
        _list: ITableInterface[],
        _metricMap: ITableMetricMap[],
      ): ITableData[] => {
        return _list?.map((mainGenreItem: ITableInterface) => {
          const {
            genre_name = '',
            genre_cn_name = '',
            children = [],
            metric_list: fetchParentList = [],
            top_games,
          } = mainGenreItem || {};
          return {
            mainGenre: genre_name,
            mainGenreCn: genre_cn_name,
            searchKey: genre_name,
            searchKeyCn: genre_cn_name,
            metric_list: getMetricFormat(fetchParentList, _metricMap),
            children: (children || [])?.map((subGenreItem: ITableInterface) => {
              const {
                name = '',
                cn_name = '',
                metric_list: fetchMetricList = [],
                top_games = [],
              } = subGenreItem;
              return {
                mainGenre: genre_name,
                mainGenreCn: genre_cn_name,
                subGenre: name,
                subGenreCn: cn_name,
                metric_list: getMetricFormat(fetchMetricList, _metricMap),
                topGames: top_games,
              };
            }),
            topGames: top_games,
          };
        });
      };
      return {
        main: (fetchData: ITableInterface[], metricMap: ITableMetricMap[]) =>
          getOriginalTableDataWhenMain(fetchData, metricMap),
        sub: (fetchData: ITableInterface[], metricMap: ITableMetricMap[]) =>
          getOriginalTableDataWhenSub(fetchData, metricMap),
        mainsub: (fetchData: ITableInterface[], metricMap: ITableMetricMap[]) =>
          getOriginalTableDataWhenMainSub(fetchData, metricMap, 'mainsub'),
      };
    },
    getExpandedTableCol: (type: string, filter?: any) => {
      const getConfig = (_type: string, _filter?: any) => {
        const header = [
          {
            type: 'checkbox',
            positionType: 'checkbox',
            key: 'checkbox',
            isLabelHide: true,
            width: 36,
          },
          {
            title: 'dbi_sub_genre',
            type: 'text',
            positionType: 'first',
            key: 'sub_genre',
            hasSort: false,
            defaultSort: false,
            hasCheckbox: true,
            // rank占位，开展行才能对齐
            hasRank: true,
            canBeClick: false,
          },
        ];
        const footer = [
          {
            type: 'action',
            title: 'Actions',
            key: 'Details',
            // 表示位置
            positionType: 'special',
            // 跳转使用到的key
            clickKey: 'sub_genre',
            linkUrl: '',
            align: 'center',
            childWidth: 100,
            childAlign: 'left',
          },
        ];
        const topRevenue = [
          {
            type: 'topRevenue',
            title:
              _type === 'mobile'
                ? 'dbi_genre_profile_top_game_revenue'
                : 'dbi_genre_profile_top_game_digital_revenue',
            key: 'topRevenue',
            width: 260,
            positionType: 'topRevenue',
          },
        ];
        const metricContent = _that.common.getCommonTableMetricList(_type, _filter);
        return [
          ...header,
          ...(metricContent.main || []).map((item: IPageConfigTableMetric) =>
            Object.assign(item, {
              // 表示位置
              positionType: 'metric',
            }),
          ),
          ...(_type !== 'total' ? topRevenue : []),
          ...footer,
        ];
      };
      return {
        main: getConfig(type, filter),
        sub: null,
        mainsub: null,
      };
    },
    getLineChartData: () => {
      const lineChartDataFormatter = ({ list, metricMap, isCn }: IGetLineDataParams) => {
        const _obj: any = {};
        metricMap?.forEach((metricItem: any) => {
          _obj[metricItem.value] = [];
        });
        list?.forEach((item: ILineChartInterface) => {
          const { date, genre_tops = [] } = item;
          genre_tops?.forEach((genreItemByDay: ITableInterface) => {
            const { name, name_cn, metric_list = [] } = genreItemByDay;
            const targetName = isCn ? name_cn : name;
            metric_list?.forEach((metricItem) => {
              const { metric = '', value } = metricItem;
              const targetArr = _obj[metric] || [];
              const findGenre = targetArr?.find(
                (genreItem: ILineChartData) => genreItem.nameEn === name,
              );
              const pushItem = {
                time: date,
                value,
              };
              if (findGenre) {
                findGenre.history.push(pushItem);
              } else {
                targetArr.push({
                  name: targetName,
                  nameEn: name,
                  nameCn: name_cn,
                  id: name,
                  history: [pushItem],
                  cover: '',
                });
              }
            });
          });
        });
        return _obj;
      };
      return {
        main: (params: IGetLineDataParams) => lineChartDataFormatter(params),
        sub: (params: IGetLineDataParams) => lineChartDataFormatter(params),
        mainsub: (params: IGetLineDataParams) => lineChartDataFormatter(params),
      };
    },
    getLineChartDataMobile: () => {
      const lineChartDataFormatter = ({
        list,
        metricMap,
        isCn,
        sourceMetrics,
        genre_tab,
        dealMap,
      }: IGetLineDataParams) => {
        function getValue(arr: any, metricItem: any) {
          arr.forEach((item: any) => {
            item.value = item.metrics[`${sourceMetrics}_${metricItem}`];
          });
          const carr = JSON.parse(JSON.stringify(arr)).sort(
            (a: any, b: any) => new Date(b.time).valueOf() - new Date(a.time).valueOf(),
          );
          return carr;
        }
        const _obj: any = {};
        const lineData: any = {};
        list?.forEach((item: any) => {
          if (genre_tab === 'main') {
            if (!lineData[item.genre]) {
              lineData[item.genre] = [{ ...item, time: item.date }]; //
            } else {
              lineData[item.genre].push({
                ...item,
                time: item.date,
              });
            }
          } else {
            if (!lineData[item.sub_genre]) {
              lineData[item.sub_genre] = [{ ...item, time: item.date }];
            } else {
              lineData[item.sub_genre].push({
                ...item,
                time: item.date,
              });
            }
          }
        });
        metricMap?.forEach((metricItem: any) => {
          _obj[metricItem.value] = Object.keys(lineData).map((key) => {
            const mainGenre = lineData[key][0].genre;
            const genreOption = dealMap.fn(dealMap.data, dealMap.type, mainGenre);
            const subGenreOption = dealMap.fn(dealMap.data, dealMap.type, mainGenre, key);
            let name = `${mainGenre}-${key}`;
            let cname = `${genreOption?.genre_cn || mainGenre}-${
              subGenreOption?.genre_cn || key
            }`;
            if (genre_tab === 'main') {
              name = mainGenre;
              cname = genreOption?.genre_cn || mainGenre;
            } else if (genre_tab === 'sub') {
              name = key;
              cname = subGenreOption?.genre_cn || key;
            }
            return {
              name: isCn ? cname : name,
              id: name,
              nameEn: name,
              nameCn: cname,
              history: getValue(lineData[key], metricItem.value),
              cover: '',
            };
          });
        });
        return _obj;
      };
      return {
        main: (params: IGetLineDataParams) => lineChartDataFormatter(params),
        sub: (params: IGetLineDataParams) => lineChartDataFormatter(params),
        mainsub: (params: IGetLineDataParams) => lineChartDataFormatter(params),
      };
    },
  },
  mobile: {
    lineMetricList: () => _that.common.getCommonLineMetricList('mobile'),
    tableMetricList: (filter?: any) => _that.common.getCommonTableMetricList('mobile', filter),
    firstCol: () => _that.common.getCommonFirstColConfig(),
    specialCol: () => _that.common.getCommonSepcialColConfig(),
    keyMap: () => _that.common.getCommonKeyMap('mobile'),
    tableFormatter: () => _that.common.getTableData(),
    lineChartFormatter: () => _that.common.getLineChartDataMobile(),
    expandedTableCol: (entityType: IEntityType, filter?: any) =>
      _that.common.getExpandedTableCol(entityType, filter),
  },
  pc: {
    lineMetricList: () => _that.common.getCommonLineMetricList('pc'),
    tableMetricList: () => _that.common.getCommonTableMetricList('pc'),
    firstCol: () => _that.common.getCommonFirstColConfig(),
    specialCol: () => _that.common.getCommonSepcialColConfig(),
    keyMap: () => _that.common.getCommonKeyMap('pc'),
    tableFormatter: () => _that.common.getTableData(),
    lineChartFormatter: () => _that.common.getLineChartData(),
    expandedTableCol: (entityType: IEntityType) => _that.common.getExpandedTableCol(entityType),
  },
  console: {
    lineMetricList: () => _that.common.getCommonLineMetricList('console'),
    tableMetricList: (filter: any) => _that.common.getCommonTableMetricList('console', filter),
    firstCol: () => _that.common.getCommonFirstColConfig(),
    specialCol: () => _that.common.getCommonSepcialColConfig(),
    keyMap: () => _that.common.getCommonKeyMap('console'),
    tableFormatter: () => _that.common.getTableData(),
    lineChartFormatter: () => _that.common.getLineChartData(),
    expandedTableCol: (entityType: IEntityType) => _that.common.getExpandedTableCol(entityType),
  },
  total: {
    lineMetricList: () => _that.common.getCommonLineMetricList('total'),
    tableMetricList: (filter: any) => _that.common.getCommonTableMetricList('total', filter),
    firstCol: () => _that.common.getCommonFirstColConfig(),
    secondCol: () => _that.common.getCommonSecondColConfig(),
    specialCol: () => _that.common.getCommonSepcialColConfig(),
    keyMap: () => _that.common.getCommonKeyMap('total'),
    tableFormatter: () => _that.common.getTableData(),
    lineChartFormatter: () => _that.common.getLineChartDataMobile(),
    expandedTableCol: (entityType: IEntityType, filter?: any) =>
      _that.common.getExpandedTableCol(entityType, filter),
  },
};

const _that = MARKET_GENRE_CONFIG;
